import { QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, isApiError } from './client'
import type { FeedCountsParams, FeedParams, Platform, PostFavorites, PostMeasurement, Role, SourcePostsParams } from './types'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(isApiError(err) && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
})

export const keys = {
  config: ['config'] as const,
  team: ['team'] as const,
  feed: (params: FeedParams) => ['feed', params] as const,
  feedCounts: (params: FeedCountsParams) => ['feed', 'counts', params] as const,
  feedAll: ['feed'] as const,
  post: (id: number, measurement?: PostMeasurement) => measurement
    ? ['post', id, measurement] as const : ['post', id] as const,
  sources: ['sources'] as const,
  sourcesAll: ['sources', 'all'] as const,
  source: (id: number) => ['source', id] as const,
  sourceAll: ['source'] as const,
  sourcePosts: (id: number, params: SourcePostsParams) => ['source', id, 'posts', params] as const,
  sourcePreviewAll: ['source-preview'] as const,
  sourcePreview: (q: string, platform: Platform | null) => ['source-preview', q, platform] as const,
  postAll: ['post'] as const,
  collections: ['collections'] as const,
  favorites: ['favorites'] as const,
  favorite: (id: number) => ['favorites', id] as const,
  postFavorites: (postId: number) => ['post-favorites', postId] as const,
  postFavoritesAll: ['post-favorites'] as const,
}

export function useConfig() {
  return useQuery({ queryKey: keys.config, queryFn: ({ signal }) => api.config(signal), staleTime: Infinity })
}

export function useTeam() {
  return useQuery({ queryKey: keys.team, queryFn: ({ signal }) => api.team(signal) })
}

export function useCreateInvite() {
  return useMutation({ mutationFn: api.createInvite })
}

export function useRemoveMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.removeMember,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.team }),
  })
}

export function useSetRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ telegramId, role }: { telegramId: number; role: Role }) => api.setRole(telegramId, role),
    // И после ошибки: человека могли убрать или повысить с другого устройства.
    onSettled: () => qc.invalidateQueries({ queryKey: keys.team }),
  })
}


export function useFeed(params: FeedParams) {
  return useInfiniteQuery({
    queryKey: keys.feed(params),
    queryFn: ({ pageParam, signal }) => api.feed({ ...params, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    // Лента меняется раз в несколько часов: возврат из карточки не перезагружает страницы.
    staleTime: 5 * 60_000,
  })
}

/** Счётчики вкладок «Видео» и «Тексты»: обе вкладки без загрузки обеих лент. */
export function useFeedCounts(params: FeedCountsParams) {
  return useQuery({
    queryKey: keys.feedCounts(params),
    queryFn: ({ signal }) => api.feedCounts(params, signal),
    staleTime: 5 * 60_000,
  })
}

export function usePost(id: number, measurement?: PostMeasurement) {
  return useQuery({
    queryKey: keys.post(id, measurement),
    queryFn: ({ signal }) => api.post(id, signal, measurement),
    staleTime: 5 * 60_000,
    refetchInterval: (query) => {
      const post = query.state.data
      if (!post || query.state.error || post.kind !== 'reel' || post.transcript_status === 'no_speech' || (!post.transcript_status && post.no_speech)) return false
      const busy = (status: string | undefined) => status === 'pending' || status === 'processing'
      return busy(post.transcript_status) || (post.transcript_status === 'ready' && post.language === 'en' && busy(post.translation_status)) ? 2500 : false
    },
    enabled: Number.isFinite(id) && id > 0,
  })
}

/** Убрать из «Добавленных»: меняются лента, счётчики и карточка. Не ждём их —
 *  иначе блок «Добавленные» в карточке исчезнет раньше, чем сработает onSuccess кнопки. */
export function useRemoveManual() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (postId: number) => api.removeManual(postId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.feedAll })
      void qc.invalidateQueries({ queryKey: keys.postAll })
    },
  })
}

/** Ролик из кэша — в чат с ботом (Mini App). */
export function useSendPost() {
  return useMutation({ mutationFn: (id: number) => api.sendPost(id) })
}

export function prefetchPost(qc: QueryClient, id: number, measurement?: PostMeasurement): void {
  void qc.prefetchQuery({ queryKey: keys.post(id, measurement), queryFn: ({ signal }) => api.post(id, signal, measurement), staleTime: 5 * 60_000 })
}


/** Без убранных. `includeRemoved` — с ними: страница «Источники» прячет их сама. */
export function useSources({ includeRemoved = false }: { includeRemoved?: boolean } = {}) {
  return useQuery({
    queryKey: includeRemoved ? keys.sourcesAll : keys.sources,
    queryFn: ({ signal }) => api.sources(includeRemoved ? { include_removed: true } : {}, signal),
  })
}

export function useSource(id: number) {
  return useQuery({
    queryKey: keys.source(id),
    queryFn: ({ signal }) => api.source(id, signal),
    enabled: Number.isSafeInteger(id) && id > 0,
  })
}

export function useSourcePosts(id: number, params: SourcePostsParams) {
  return useInfiniteQuery({
    queryKey: keys.sourcePosts(id, params),
    queryFn: ({ pageParam, signal }) => api.sourcePosts(id, { ...params, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 5 * 60_000,
  })
}

export function useSourcePreview(q: string, platform: Platform | null) {
  return useQuery({
    queryKey: keys.sourcePreview(q, platform),
    queryFn: ({ signal }) => api.sourcePreview({ q, platform: platform ?? undefined }, signal),
    enabled: q.trim() !== '',
    staleTime: 60_000,
  })
}

/** Добавить, пауза, вернуть, убрать: меняются список, подборки, лента и пометка репоста в карточке.
 *  Ждём только список источников: лента перечитывается в фоне, кнопка не висит. */
function useSourceMutation<A, R>(fn: (args: A) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const queryKey of [keys.sourcePreviewAll, keys.sourceAll, keys.collections, keys.feedAll, keys.postAll]) {
        void qc.invalidateQueries({ queryKey })
      }
      return qc.invalidateQueries({ queryKey: keys.sources })
    },
  })
}

export function useAddSource() {
  return useSourceMutation(api.addSource)
}

export function usePauseSource() {
  return useSourceMutation(api.pauseSource)
}

export function useResumeSource() {
  return useSourceMutation(api.resumeSource)
}

export function useRemoveSource() {
  return useSourceMutation(api.removeSource)
}

export function useCollections() {
  return useQuery({ queryKey: keys.collections, queryFn: ({ signal }) => api.collections(signal) })
}

function useCollectionMutation<A, R>(fn: (args: A) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: keys.collections }),
        qc.invalidateQueries({ queryKey: keys.sources }),
        qc.invalidateQueries({ queryKey: keys.sourceAll }),
        qc.invalidateQueries({ queryKey: keys.feedAll }),
      ]),
  })
}

export function useCreateCollection() {
  return useCollectionMutation((name: string) => api.createCollection(name))
}

export function useRenameCollection() {
  return useCollectionMutation(({ id, name }: { id: number; name: string }) => api.updateCollection(id, { name }))
}

export function useDeleteCollection() {
  return useCollectionMutation((id: number) => api.deleteCollection(id))
}

export function useSetCollectionSources() {
  return useCollectionMutation(({ id, sourceIds }: { id: number; sourceIds: number[] }) =>
    api.setCollectionSources(id, sourceIds),
  )
}


export function useFavorites() {
  return useQuery({ queryKey: keys.favorites, queryFn: ({ signal }) => api.favorites(signal) })
}

export function useFavorite(id: number) {
  return useQuery({
    queryKey: keys.favorite(id),
    queryFn: ({ signal }) => api.favorite(id, signal),
    enabled: Number.isFinite(id) && id > 0,
  })
}

export function usePostFavorites(postId: number) {
  return useQuery({
    queryKey: keys.postFavorites(postId),
    queryFn: ({ signal }) => api.postFavorites(postId, signal),
    enabled: Number.isFinite(postId) && postId > 0,
  })
}

/** Список групп и сердечки; `groupId` — ещё и открытая группа. */
function invalidateFavorites(qc: QueryClient, groupId?: number) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: keys.favorites, exact: true }),
    qc.invalidateQueries({ queryKey: keys.postFavoritesAll }),
    groupId !== undefined ? qc.invalidateQueries({ queryKey: keys.favorite(groupId) }) : undefined,
  ])
}

/** `groupOf` — какую открытую группу обновить после изменения. */
function useFavoritesMutation<A, R>(fn: (args: A) => Promise<R>, groupOf?: (args: A) => number) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: (_data, args) => invalidateFavorites(qc, groupOf?.(args)) })
}

export function useCreateFavorite() {
  return useFavoritesMutation((name: string) => api.createFavorite(name))
}

export function useRenameFavorite() {
  return useFavoritesMutation(
    ({ id, name }: { id: number; name: string }) => api.renameFavorite(id, name),
    ({ id }) => id,
  )
}

export function useDeleteFavorite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteFavorite(id),
    // Открытую удалённую группу не трогаем: экран уходит со страницы, и кэш
    // сам соберётся. Иначе она успеет перезапроситься и мигнуть 404.
    onSuccess: () => {
      void invalidateFavorites(qc)
    },
  })
}

interface Membership {
  groupId: number
  postId: number
  inGroup: boolean
}

/** Положить пост в группу или убрать. Сердечко меняется сразу, при ошибке возвращается. */
export function useToggleFavorite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, postId, inGroup }: Membership) =>
      inGroup ? api.addToFavorite(groupId, postId) : api.removeFromFavorite(groupId, postId),
    onMutate: async ({ groupId, postId, inGroup }: Membership) => {
      const key = keys.postFavorites(postId)
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<PostFavorites>(key)
      const ids = new Set(prev?.group_ids ?? [])
      if (inGroup) ids.add(groupId)
      else ids.delete(groupId)
      qc.setQueryData<PostFavorites>(key, { group_ids: [...ids].sort((a, b) => a - b) })
      return { prev }
    },
    onError: (_err, { postId }, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.postFavorites(postId), ctx.prev)
    },
    onSettled: (_data, _err, { groupId }) => invalidateFavorites(qc, groupId),
  })
}

export function useCreateAgentLink() {
  return useFavoritesMutation(
    (groupId: number) => api.createAgentLink(groupId),
    (groupId) => groupId,
  )
}

export function useRevokeAgentLink() {
  return useFavoritesMutation(
    (groupId: number) => api.revokeAgentLink(groupId),
    (groupId) => groupId,
  )
}
